import React, { useState } from "react";
import Modal from "./Modal.tsx";
import { updateAdministeredMedication } from "../../services/medication.js";
import toast from "react-hot-toast";

// CCS-377: lets an admin correct an administered medication after a shift has
// ended — the administration type, signature, notes and the dose. The dose is
// stored as a per-administration override, so it only affects this shift's
// record (never the medication master or other shifts).
const ADMINISTRATION_OPTIONS = [
  "Administered",
  "Prompted",
  "Declined",
  "Assisted",
  "Required",
  "Not Applicable",
];

// get-shift-details returns a zero-UUID as the medication id when the underlying
// medication has been deleted (the administration row is kept for audit). Such a
// record cannot be edited — there is no live medication_group_items row to key
// the update against.
const DELETED_MEDICATION_ID = "00000000-0000-0000-0000-000000000000";

export const isEditableMedicationId = (id) =>
  Boolean(id) && id !== DELETED_MEDICATION_ID;

// The Lambda emits the literal "N/A" when no dose is recorded; treat that as
// empty so it is neither shown in the field nor persisted as an override.
const cleanDose = (dose) => (dose && dose !== "N/A" ? dose : "");

export const EditAdministeredMedicationModal = ({
  isOpen,
  onClose,
  shiftId,
  serviceUserId,
  administration,
  onSaved,
}) => {
  const details = administration?.administrationDetails ?? {};
  const medicationName =
    administration?.medication?.name ||
    administration?.medicationName ||
    "medication";
  const medicationItemId =
    administration?.medication?.id || administration?.medicationId;

  const [administrationType, setAdministrationType] = useState(
    details.administrationType ?? "Administered"
  );
  const [signature, setSignature] = useState(details.signature ?? "");
  const [notes, setNotes] = useState(details.notes ?? "");
  const [dose, setDose] = useState(cleanDose(administration?.medication?.dose));
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEditableMedicationId(medicationItemId)) {
      // A deleted medication has no live id to update against.
      setError("This medication record can no longer be edited");
      return;
    }
    if (!administrationType) {
      setError("Administration type is required");
      return;
    }
    if (!signature.trim()) {
      setError("Signature is required");
      return;
    }
    setError("");

    // Only send fields that have a value. The backend COALESCEs NULL/omitted
    // fields to the existing value, so an empty field preserves what's stored
    // rather than blanking it (consistent for notes and dose).
    const administrationDetails = {
      administrationType,
      signature: signature.trim(),
    };
    const trimmedNotes = notes.trim();
    if (trimmedNotes) {
      administrationDetails.notes = trimmedNotes;
    }

    const payload = {
      shiftId: Number(shiftId),
      serviceUserId,
      medicationItemId,
      administrationDetails,
    };
    const trimmedDose = dose.trim();
    if (trimmedDose) {
      payload.dose = trimmedDose;
    }

    setIsSaving(true);
    try {
      await updateAdministeredMedication(payload, serviceUserId);
      toast.success("Medication record updated");
      onSaved?.();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update medication record");
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={`Edit ${medicationName}`}
      size="md"
      onCancel={handleClose}
      isOpen={isOpen}
    >
      <form onSubmit={handleSubmit}>
        <div className="form__group">
          <label htmlFor="editMedAdminType">Administration type</label>
          <div className="form__input">
            <select
              id="editMedAdminType"
              value={administrationType}
              onChange={(e) => setAdministrationType(e.target.value)}
            >
              {/* Keep the current value selectable even if it is not one of the
                  standard options (e.g. legacy data). */}
              {administrationType &&
                !ADMINISTRATION_OPTIONS.includes(administrationType) && (
                  <option value={administrationType}>{administrationType}</option>
                )}
              {ADMINISTRATION_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form__group">
          <label htmlFor="editMedDose">Dose</label>
          <div className="form__input">
            <input
              id="editMedDose"
              type="text"
              value={dose}
              onChange={(e) => setDose(e.target.value)}
              placeholder="e.g. 10mg"
            />
          </div>
        </div>

        <div className="form__group">
          <label htmlFor="editMedSignature">
            Signature <span className="required">*</span>
          </label>
          <div className="form__input">
            <input
              id="editMedSignature"
              type="text"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="Enter your full name"
            />
          </div>
        </div>

        <div className="form__group">
          <label htmlFor="editMedNotes">Notes</label>
          <div className="form__input">
            <textarea
              id="editMedNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional observations or notes..."
            />
          </div>
        </div>

        {error && <span className="form__error">{error}</span>}
        <button
          type="submit"
          className="btn btn__primary btn--block"
          disabled={isSaving}
        >
          {isSaving ? "Loading..." : "Save changes"}
        </button>
      </form>
    </Modal>
  );
};
